import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, getDay, parse, startOfWeek, addMinutes } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React, { useEffect, useState } from "react";
import { AppointmentServiceInternal } from "../../services/appointment.service";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { BUTTONS, APPOINTMENT_TYPE_ID, PATIENT_MATCH_MAX_SCORE } from "../../constants";
import { Card } from "react-bootstrap";
import sheduleAppointment from "../../assets/images/Appointment-logo.png";
import { isValid, parseISO } from "date-fns";
import { ProviderServiceInternal } from "../../services/provider.service";
import userStore from "../../store/store";
import { convertToLocalTime, today } from "../Shared/Share";
import { toast } from "react-toastify";
import { PatientInfoRequest, PatientInfoResponse } from "../../models/patientInfoDtos";

interface SheduleAppointmentProps {
  changeStep: (data: any) => void;
  updateFormData: (data: any) => void;
  formData: any;
  setPrimaryProviderMessage: (data: any) => void;
  setConvertedTime: (data: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  isReschedule: boolean;
  currentAppointment: any;
}

interface AppointmentOption {
  id: string;
  appointment_type: string;
  appointment_type_time: string;
}

interface Provider {
  id: number;
  name: string;
}

interface TimeSlot {
  id: number;
  title: string;
  start: Date;
  end: Date;
  localStartTime: string;
  utcStartTime: string;
  isSelected: boolean;
}

interface Facility {
  id: number;
  athenaDepartmentId: number;
  officer_name: string;
  name: string;
  timeZone: string;
  address_zipcode: string;
}

const currentDate = new Date();

const SheduleAppointment: React.FC<SheduleAppointmentProps> = (props) => {
  const user: any = userStore();
  const [options, setOptions] = useState<AppointmentOption[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date(props.currentAppointment?.appointmentdatetime ?? currentDate));
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<number | null>(null);
  const [appointmentTypeTime, setAppointmentTypeTime] = useState<string | undefined>("");
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [availableProviders, setAvailableProviders] = useState<Provider[]>([]);
  const [fetchingAppointmentTypes, setFetchingAppointmentTypes] = useState(false);
  const [fetchingProviders, setFetchingProviders] = useState(false);
  const [fetchingTimeSlots, setFetchingTimeSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [currentFacility, setCurrentFacility] = useState<Facility | null>(null);
  const [userFacilities, setUserFacilities] = useState<Facility[] | null>(null);
  const [facilityZipCode, setFacilityZipCode] = useState<string>("");
  const [availableEvents, setAvailableEvents] = useState<TimeSlot[] | undefined>([]);
  const [hasMultipleFacilities, setHasMultipleFacilities] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [currentAppointmentDateTime, setCurrentAppointmentDateTime] = useState<string | null>(null);
  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
    getDay,
    locales: enUS,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      facilityid: "",
      appointmenttypeid: "",
      providerid: "",
      appointmentDate: currentDate,
      appointmentTime: "",
      athenaAppointmentId: 0
    },
    resolver: yupResolver(
      yup.object().shape({
        facilityid: yup.string().notRequired(),
        appointmenttypeid: yup.string().required("Appointment Type is required"),
        providerid: yup.string().required("Provider is required"),
        appointmentDate: yup.date().required("Appointment Date is required"),
        appointmentTime: yup.string().required("Appointment Time is required"),
        athenaAppointmentId: yup.number().required("Something went wrong. Please try again.")
      })
    ),
  });

  const eventPropGetter = (event: TimeSlot) => {
    const backgroundColor = event.isSelected ? "green" : "rgb(49 116 173)"
    return {
      style: {
        backgroundColor
      },
    };
  }

  const formatDateForDisplay = (dateInput: Date | string) => {
    const date = typeof dateInput === "string" ? new Date(dateInput + "Z") : dateInput;
    const weekdayName = date.toLocaleString("en-US", { weekday: "short" })
    const monthName = date.toLocaleString("en-US", { month: "long" });
    const day = date.getDate();
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
    return `${weekdayName}, ${monthName} ${day} @ ${time}`;
  };

  const mapToTimeSlot = (item: any): TimeSlot => {
    return {
      id: item.athenaAppointmentId,
      title: ``,
      start: new Date(item.startTime + "Z"),
      end: addMinutes(new Date(item.startTime + "Z"), Number(appointmentTypeTime)),
      localStartTime: convertToLocalTime(item.startString),
      utcStartTime: item.startString,
      isSelected: false
    }
  };

  const fetchAppointmentTypesByFacility = async (facilityId: number) => {
    try {
      setFetchingAppointmentTypes(true);
      const response = await AppointmentServiceInternal.getAppointmentTypesByFacility(facilityId);
      if (response?.data.isSuccess) {
        // Sort the options alphabetically by appointment_type
        const sortedOptions = response.data.data.sort(
          (a: AppointmentOption, b: AppointmentOption) =>
            a.appointment_type.localeCompare(b.appointment_type)
        );
        setOptions(sortedOptions);
      }
    } catch (error) {
      console.error("Error fetching data from API:", error);
    } finally {
      setFetchingAppointmentTypes(false);
    }
  };

  const fetchProviders = async (appointmentTypeId: number) => {
    setFetchingProviders(true);
    try {
      const response = await ProviderServiceInternal.getProvidersByAppointmentType(appointmentTypeId);
      if (response?.data.isSuccess) {
        const providersList: Provider[] = response.data.data.map((user: any) => ({
          id: user.userId,
          name: user.providerName
        }));
        setAvailableProviders(providersList);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
    } finally {
      setFetchingProviders(false);
    }
  };

  const fetchTimeSlotsFromApi = async () => {
    try {
      setFetchingTimeSlots(true);
      const facilityId = currentFacility?.athenaDepartmentId ?? 0;
      const localDateTime = startDate.toDateString();
      const patientName = (props.formData.inmateFirstName.trim() + " " + props.formData.inmateLastName.trim());
      const patientDob = props.formData.inmatedob;
      const patientZipCode = props.formData.ZipCode;
      const providerName = selectedProvider?.name ?? null;
      const apptResponse = await AppointmentServiceInternal.getAvailableAppointments(selectedAppointmentType, facilityId, localDateTime, patientName, patientDob, patientZipCode, providerName);
      if (apptResponse?.data.isSuccess) {
        let timeSlotsByWeek: TimeSlot[] = apptResponse?.data.data.map(mapToTimeSlot);
        setAvailableEvents(timeSlotsByWeek);
        props.setPrimaryProviderMessage(apptResponse?.data.message);
      }
      else {
        toast.info("No availability found.")
      }
    }
    catch (error) {
      console.error('Error fetching data from API:', error);
    }
    finally {
      setFetchingTimeSlots(false);
    }
  };

  const handleCalendarNavigate = (date: Date) => {
    const startOfNewDate = new Date(date);
    startOfNewDate.setHours(0, 0, 0, 0);
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    if (startOfNewDate < startOfToday) {
      toast.warn("Cannot view past availability.")
      return;
    }
    setStartDate(date);
    setValue('appointmentDate', date);
  };

  const handleSelectAppointmentSlot = (slotInfo: TimeSlot) => {
    if (selectedTimeSlot === slotInfo) {
      selectedTimeSlot.isSelected = false;
      slotInfo.isSelected = false;
      setSelectedTimeSlot(null);
      setValue("appointmentTime", "", { shouldValidate: true });
      setValue("appointmentDate", startDate);
      setValue("athenaAppointmentId", 0);
      props.setConvertedTime("");
      setSelectedDate(startDate);
      setSelectedTime("");
      return;
    }
    if (selectedTimeSlot) {
      selectedTimeSlot.isSelected = false;
    }
    slotInfo.isSelected = true;
    setSelectedTimeSlot(slotInfo);
    setValue("appointmentTime", slotInfo.utcStartTime, { shouldValidate: true });
    setValue("appointmentDate", slotInfo.start);
    setValue("athenaAppointmentId", slotInfo.id);
    props.setConvertedTime(slotInfo.localStartTime);
    setSelectedDate(slotInfo.start);
    setSelectedTime(formatDateForDisplay(slotInfo.start));
  };

  const onFacilityChange = async (id: number) => {
    let selectedFacility = userFacilities?.find(f => f.id === id);
    setValue("facilityid", id.toString());
    setCurrentFacility(selectedFacility ?? null);
    setFacilityZipCode(selectedFacility?.address_zipcode ?? "");
    setSelectedTime("");
    setAvailableEvents([]);
    setAvailableProviders([]);
    setValue("providerid", "");
    setSelectedProvider(null);
    setSelectedAppointmentType(null);
    setValue("appointmenttypeid", "");
    setOptions([]);
    await fetchAppointmentTypesByFacility(id);
  }

  const onAppointmentTypeChange = async (event: SelectChangeEvent) => {
    const selectedType = parseInt(event.target.value, 10);
    const appointmentType = options.find(o => o.id.toString() === selectedType.toString());

    setAvailableEvents([]);
    setValue("providerid", "");
    setSelectedProvider(null);
    setAvailableProviders([]);
    setSelectedAppointmentType(selectedType);
    setAppointmentTypeTime(appointmentType?.appointment_type_time);
    setSelectedTime("");

    await fetchProviders(selectedType);
  };

  const onProviderChange = async (event: SelectChangeEvent) => {
    const selectedProviderId = parseInt(event.target.value, 10);
    const prov = availableProviders.find(p => p.id === selectedProviderId);

    setSelectedTime("");
    setAvailableEvents([]);
    setValue("providerid", `${prov?.id ?? ""}`);
    setSelectedProvider(prov ?? null);
  };

  const onSubmit = async (data: any) => {
    const selectedAppointmentTypeId = Number(data.appointmenttypeid);
    const selectedAppointmentType = options.find(
      (option: any) => option.id === selectedAppointmentTypeId
    );
    
    const patientInfoRequest: PatientInfoRequest = {
      firstName: props.formData.inmateFirstName,
      lastName: props.formData.inmateLastName,
      dateOfBirth: props.formData.inmatedob,
      zipCode: facilityZipCode,
      correctionalFacilityId: currentFacility?.id ?? user.getUser().id_correctional
    };
    let patientMatches: PatientInfoResponse[] | null = [];
    let athenaPatientId = 0;

    if (!props?.isReschedule) {
      try {
        props.setIsLoading(true);
        const response = await AppointmentServiceInternal.getPossiblePatientMatches(patientInfoRequest);
        patientMatches = response.data.data;

        if(!patientMatches) {
          athenaPatientId = -1;
          patientMatches = [];
        }
        if (patientMatches?.length === 1) {
          let match = patientMatches[0];

          if (match.score >= PATIENT_MATCH_MAX_SCORE) {
            athenaPatientId = Number(match.localPatientId);
            patientMatches = [];
          }
        }
        props.setIsLoading(false);
      } catch (error) {
        props.setIsLoading(false);
        console.error("Error fetching data from API:", error);
      }
    }

    const combinedData = {
      ...data,
      appointmentDate: selectedDate,
      appointmenttypename: selectedAppointmentType?.appointment_type,
      providerId: selectedProvider?.id,
      providername: selectedProvider?.name,
      facilityName: currentFacility?.name,
      correctionalFacilityId: currentFacility?.id,
      zipCode: facilityZipCode,
      athenaPatientId: athenaPatientId,
      patientMatches: patientMatches
    };
    props.updateFormData(combinedData);
    props.changeStep(1);
  };

  useEffect(() => {
    if (!user) return;
    const defaultFacilityId = user.getUser().id_correctional;
    const facilities: Facility[] = user.getFacilities().sort((a: Facility, b:Facility) => a.name.localeCompare(b.name));
    const defaultFacility = facilities.find(f => f.id === defaultFacilityId);

    setValue("facilityid", defaultFacilityId.toString());
    setCurrentFacility(defaultFacility ?? null);
    setFacilityZipCode(defaultFacility?.address_zipcode ?? "");
    setUserFacilities(facilities);
    setHasMultipleFacilities(facilities.length > 1);

    const fetchAppointmentTypes = async () => {
      try {
        setFetchingAppointmentTypes(true);
        const response = await AppointmentServiceInternal.appointmenttype();
        if (response?.data.isSuccess) {
          // Sort the options alphabetically by appointment_type
          const sortedOptions = response.data.data.sort(
            (a: AppointmentOption, b: AppointmentOption) =>
              a.appointment_type.localeCompare(b.appointment_type)
          );
          if (props.isReschedule) {
            const appointmentType = sortedOptions.find((o: { id: { toString: () => any; }; }) => o.id.toString() === props.currentAppointment.appointmenttypeid.toString());
            setAppointmentTypeTime(appointmentType?.appointment_type_time);
            setValue("appointmenttypeid", props.currentAppointment.appointmenttypeid);
            setSelectedAppointmentType(props.currentAppointment.appointmenttypeid);
            fetchProviders(props.currentAppointment.appointmenttypeid);
            setValue("providerid", `${props.currentAppointment.providerId}`);
            setSelectedProvider({ id: props.currentAppointment.providerId, name: props.currentAppointment.providerName });
            setValue("appointmentDate", props.currentAppointment.appointmentdatetime);
            setCurrentAppointmentDateTime(formatDateForDisplay(props.currentAppointment.appointmentdatetime));
          }
          setOptions(sortedOptions);
        }
      } catch (error) {
        console.error("Error fetching data from API:", error);
      } finally {
        setFetchingAppointmentTypes(false);
      }
    };
    fetchAppointmentTypes();
  }, [props.currentAppointment?.appointmentdatetime, props.currentAppointment?.appointmenttypeid, props.currentAppointment?.providerId, props.currentAppointment?.providerName, props.isReschedule, setValue, user]);

  useEffect(() => {
    if (startDate && selectedProvider && selectedAppointmentType) {
      fetchTimeSlotsFromApi();
    }
  }, [selectedAppointmentType, selectedProvider, startDate]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="add-patient-form">
        <div className="d-flex justify-content-center">
          <Card
            style={{
              width: "100%",
              maxHeight: "calc(100vh - 113px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Card.Body style={{ flex: 1, overflowY: "auto" }}>
              <Card.Title>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box>
                      <img
                        alt=""
                        src={sheduleAppointment}
                        style={{ height: 32, width: 32 }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{ fontWeight: "bold" }}
                    >
                      {props?.isReschedule
                        ? "Reschedule an Appointment"
                        : "Schedule an Appointment"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body1" className="form-control" sx={{ border: "none" }}>
                      New Appointment Date: {selectedTime || "--"}
                    </Typography>
                  </Stack>
                </Box>
              </Card.Title>
              <div className="referral-form">
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                  {userFacilities &&
                    currentFacility &&
                    userFacilities.length > 1 && (
                      <Grid item xs={12} md={4}>
                        <div className="form-label">
                          <label>
                            Facility<span className="text-danger">*</span>
                          </label>
                        </div>
                        <Controller
                          name="facilityid"
                          control={control}
                          defaultValue={currentFacility.id.toString()}
                          render={({ field }) => (
                            <FormControl fullWidth size="small">
                              <Autocomplete
                                size="small"
                                options={userFacilities}
                                value={currentFacility}
                                onChange={(event, newValue) => {
                                  if (newValue) onFacilityChange(newValue.id);
                                }}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                  <TextField {...params} variant="outlined" />
                                )}
                                renderOption={(props, option) => (
                                  <li {...props}>
                                    <Box
                                      component="span"
                                      title={option.name}
                                      sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {option.name}
                                    </Box>
                                  </li>
                                )}
                              />
                            </FormControl>
                          )}
                        />
                      </Grid>
                    )}
                  <Grid item xs={12} md={hasMultipleFacilities ? 4 : 6}>
                    <div className="form-label">
                      <label>
                        Appointment Type<span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="appointmenttypeid"
                      control={control}
                      defaultValue={props.formData.appointmenttypeid}
                      render={({ field }) => (
                        <>
                          <Select
                            {...field}
                            className="form-control"
                            onChange={(e) => {
                              field.onChange(e);
                              onAppointmentTypeChange(e);
                            }}
                            IconComponent={
                              fetchingAppointmentTypes
                                ? () => (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginRight: 8,
                                    }}
                                  >
                                    <CircularProgress
                                      size={20}
                                      thickness={3}
                                    />
                                  </span>
                                )
                                : ArrowDropDownIcon
                            }
                            disabled={
                              options.length === 0 ||
                              fetchingAppointmentTypes
                            }
                          >
                            <MenuItem value="" disabled>
                              Select...
                            </MenuItem>
                            {options.map((option: any) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.appointment_type}
                                {option.id !==
                                  APPOINTMENT_TYPE_ID.FIRSTTIMEBEINGSEEN &&
                                  option.id !== APPOINTMENT_TYPE_ID.FOLLOWUP &&
                                  ` (${option.appointment_type_time} minutes)`}
                              </MenuItem>
                            ))}
                          </Select>
                        </>
                      )}
                    />
                    {errors.appointmenttypeid && (
                      <p className="text-red-600 text-sm">
                        {errors.appointmenttypeid.message}
                      </p>
                    )}
                  </Grid>
                  <Grid item xs={12} md={hasMultipleFacilities ? 4 : 6}>
                    <div className="form-label">
                      <label>
                        Provider<span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="providerid"
                      control={control}
                      defaultValue={props.formData.providerId?.toString()}
                      render={({ field }) => (
                        <>
                          <Select
                            {...field}
                            className="form-control"
                            onChange={(e) => {
                              field.onChange(e);
                              onProviderChange(e);
                            }}
                            IconComponent={
                              fetchingProviders
                                ? () => (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginRight: 8,
                                    }}
                                  >
                                    <CircularProgress
                                      size={20}
                                      thickness={3}
                                    />
                                  </span>
                                )
                                : ArrowDropDownIcon
                            }
                            disabled={
                              availableProviders.length === 0 ||
                              fetchingProviders
                            }
                          >
                            {availableProviders.map((option: Provider) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </>
                      )}
                    />
                    {errors.providerid && (
                      <p className="text-red-600 text-sm">
                        {errors.providerid.message}
                      </p>
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div className="form-label">
                      <label>
                        Availability<span className="text-danger">*</span>
                      </label>
                    </div>
                    <Controller
                      name="appointmentDate"
                      control={control}
                      render={({ field }) => (
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "600px",
                            overflow: "hidden",
                            zIndex: 1,
                          }}
                        >
                          <Backdrop
                            open={!selectedProvider || fetchingTimeSlots}
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              zIndex: (theme) => theme.zIndex.drawer + 1,
                              color: "#fff",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              paddingTop: "120px",
                              backgroundColor: "rgba(0, 0, 0, 0.25)",
                              pointerEvents: "auto",
                            }}
                          >
                            {fetchingTimeSlots ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  px: 4,
                                  py: 2,
                                  borderRadius: 2,
                                  boxShadow: 3,
                                }}
                              >
                                <CircularProgress size={20} />
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "#333",
                                  }}
                                >
                                  Searching for available times...
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "#333",
                                  fontWeight: "bold",
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  px: 4,
                                  py: 2,
                                  borderRadius: 2,
                                  boxShadow: 3,
                                  textAlign: "center",
                                }}
                              >
                                Select an appointment type and a provider to
                                view the calendar.
                              </Typography>
                            )}
                          </Backdrop>
                          <Calendar
                            {...field}
                            localizer={localizer}
                            events={availableEvents ?? []}
                            defaultView="week"
                            views={["week"]}
                            selectable={false}
                            defaultDate={parseISODateTime(field.value)}
                            date={parseISODateTime(field.value)}
                            onNavigate={handleCalendarNavigate}
                            onSelectEvent={handleSelectAppointmentSlot}
                            eventPropGetter={eventPropGetter}
                            min={new Date(1970, 1, 1, 7, 0)} // 7:00 AM
                            max={new Date(1970, 1, 1, 19, 0)} // 6:00 PM
                            style={{ height: "100%", width: "100%" }}
                            dayLayoutAlgorithm="no-overlap"
                            formats={{
                              eventTimeRangeFormat: (
                                { start, end }: any,
                                culture: any,
                                localizer: {
                                  format: (
                                    arg0: any,
                                    arg1: string,
                                    arg2: any
                                  ) => any;
                                }
                              ) =>
                                `${localizer
                                  .format(start, "h:mma", culture)
                                  .replace("AM", "a")
                                  .replace("PM", "p")} - ${localizer
                                    .format(end, "h:mma", culture)
                                    .replace("AM", "a")
                                    .replace("PM", "p")}`,
                            }}
                          />
                        </div>
                      )}
                    />
                  </Grid>
                </Grid>
              </div>
            </Card.Body>
            <Card.Footer className="position-relative">
              {currentAppointmentDateTime && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <Typography variant="body1">
                    Current Appointment Date: {currentAppointmentDateTime}
                  </Typography>
                </Box>
              )}
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={6}
                flexWrap="wrap"
              >
                <Button
                  type="button"
                  variant="outlined"
                  color={props?.isReschedule ? "error" : "primary"}
                  sx={{ minWidth: 120 }}
                  onClick={() => {
                    if (!props?.isReschedule) {
                      props.updateFormData(props.formData);
                      props.changeStep(-1);
                    } else {
                      if (user.getUser().id_role === 2) {
                        window.location.href = "/facility-dashboard";
                      } else if (user.getUser().id_role === 3) {
                        window.location.href = "/itp-dashboard"
                      }
                    }
                  }}
                >
                  {props?.isReschedule ? BUTTONS.CANCEL : BUTTONS.PREVIOUS}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!selectedTime}
                  sx={{ minWidth: 120 }}
                >
                  {BUTTONS.NEXT}
                </Button>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Typography variant="body1">
                  New Appointment Date: {selectedTime || "--"}
                </Typography>
              </Box>
            </Card.Footer>
          </Card>
        </div>
      </form>
    </>
  );
};
export default SheduleAppointment;

export const parseISODateTime = (v?: string | Date) => {
  if (!v) {
    return null;
  }

  if (v instanceof Date) {
    return v;
  }

  const date = parseISO(v);
  if (isValid(date)) {
    return date;
  }

  return null;
};
